import { uniq } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import MultiContributorsAutocomplete from 'referencial/components/contributor/components/MultiContributorsAutocomplete'
import i18n from 'simple-react-i18n'
import useSessionStorage from 'utils/customHook/useSessionStorage'
import Checkbox from '../../../components/forms/Checkbox'
import Input from '../../../components/forms/Input'
import Select from '../../../components/forms/Select'
import CampaignSelect from '../../../components/forms/specific/CampaignSelect'
import { LIST_PANEL, MAP_PANEL } from '../../constants/MaterielConstants'
import { Button, Card, CardContent, Grid, Tooltip } from '@mui/material'
import { List, Map } from '@mui/icons-material'

const SimSubscriptionFilterForm = ({
    filter = {
        administrator: undefined,
        state: undefined,
        displayOutOfService: false,
        network: undefined,
        matType: undefined,
        searchValue: '',
        provider: undefined,
        displayEquipment: false,
    },
    view = LIST_PANEL,
    changeView,
    materiels = [],
    typeList = [],
    showEquipmentCheck = true,
    onValidate = () => {},
    materielType = '',
}) => {
    const {
        materielStates,
        contributors,
        networks,
    } = useSelector(store => ({
        contributors: store.ContributorReducer.contributors,
        materielStates: store.MaterielReducer.materielStates,
        networks: store.NetworkReducer.networks,
    }), shallowEqual)

    const [administrator, setAdministrator] = useSessionStorage(`${materielType}_ADMINISTRATOR`, filter.administrator)
    const [state, setState] = useSessionStorage(`${materielType}_STATE`, filter.state)
    const [displayOutOfService, setDisplayOutOfService] = useSessionStorage(`${materielType}_DISPLAY_OUT_OF_SERVICE`, filter.displayOutOfService)
    const [network, setNetwork] = useSessionStorage(`${materielType}_NETWORK`, filter.network)
    const [matType, setMatType] = useSessionStorage(`${materielType}_TYPE`, filter.matType, true)
    const [searchValue, setSearchValue] = useSessionStorage(`${materielType}_SEARCH_VALUE`, filter.searchValue)
    const [provider, setProvider] = useSessionStorage(`${materielType}_PROVIDER`, filter.provider)
    const [displayEquipment, setDisplayEquipment] = useSessionStorage(`${materielType}_DISPLAY_EQUIPMENT`, filter.displayEquipment)

    useEffect(() => {
        onValidate({
            searchValue,
            administrator,
            state,
            provider,
            network,
            matType,
            displayEquipment,
            displayOutOfService,
        })
    }, [])

    // recherche : bouton, ou touche Entrée dans un champ texte (valeur saisie passée en surcharge, l'état n'étant pas encore à jour)
    const onSearch = (changes = {}) => onValidate({
        searchValue,
        administrator,
        state,
        provider,
        network,
        matType,
        displayEquipment,
        displayOutOfService,
        ...changes,
    })

    const stateCodes = uniq(materiels.map(m => m.statusCode)).filter(code => displayOutOfService || (code !== 0 && code !== 5 && code !== 6))
    const filteredStates = stateCodes.map(code => materielStates.find(c => c.code === code)).filter(s => !!s)

    const providerIds = uniq(materiels.map(m => m.providerId))
    const filteredProviders = providerIds.map(id => contributors.find(c => c.id === id)).filter(c => !!c)

    const administratorIds = uniq(materiels.map(m => m.administratorId))
    const filteredAdministrators = administratorIds.map(id => contributors.find(c => c.id === id)).filter(c => !!c)

    const networkIds = uniq(materiels.map(m => m.networkCode))
    const filteredNetworks = networkIds.map(id => networks.find(c => c.id === id)).filter(n => !!n)

    return (
        <Card>
            <CardContent>
                <Grid container size={12} spacing={1}>
                    <Grid container size={6}>
                        <Grid size={12}>
                            <Input
                                title={i18n.search}
                                value={searchValue}
                                onChange={setSearchValue}
                                onEnterKeyPress={value => {
                                    setSearchValue(value)
                                    onSearch({ searchValue: value })
                                }}
                            />
                        </Grid>
                        <Grid size={12}>
                            <MultiContributorsAutocomplete
                                label={i18n.administrator}
                                options={filteredAdministrators}
                                values={administrator}
                                onChange={setAdministrator}
                                keyLabel='labelDisplay'
                                keyValue='id'
                            />
                        </Grid>
                        <Grid container size={12} alignItems='center'>
                            <Grid size={4}>
                                <Select
                                    label={i18n.state}
                                    options={filteredStates}
                                    value={state}
                                    onChange={setState}
                                    keyLabel='label'
                                    keyValue='code'
                                    integerValue
                                    returnNull
                                />
                            </Grid>
                            <Grid size={8}>
                                <Checkbox
                                    label={i18n.displayOutOfService}
                                    tooltip={i18n.displayOutOfService}
                                    checked={displayOutOfService}
                                    onChange={v => {
                                        setDisplayOutOfService(v)
                                        if (!v && (state === 0 || state === 5 || state === 6)) {
                                            setState(undefined)
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <Grid size={12}>
                            <CampaignSelect
                                changeFilter={() => { }}
                            />
                        </Grid>
                    </Grid>
                    <Grid container size={6}>
                        <Grid size={12}>
                            <MultiContributorsAutocomplete
                                label={i18n.provider}
                                options={filteredProviders}
                                values={provider}
                                onChange={setProvider}
                                keyLabel='labelDisplay'
                                keyValue='id'
                            />
                        </Grid>
                        <Grid size={12}>
                            <Select
                                label={i18n.network}
                                options={filteredNetworks}
                                value={network}
                                onChange={setNetwork}
                                keyLabel='name'
                                keyValue='id'
                                integerValue
                                returnNull
                            />
                        </Grid>
                        {!!typeList && (
                            <Grid size={12}>
                                <Select
                                    label={i18n.materielTypes}
                                    options={typeList}
                                    value={matType}
                                    onChange={setMatType}
                                    keyLabel='label'
                                    keyValue='id'
                                    integerValue
                                    returnNull
                                />
                            </Grid>
                        )}
                        <Grid container size={12} alignItems='center' justifyContent='space-between'>
                            <Grid size={7}>
                                {showEquipmentCheck && (
                                    <Checkbox
                                        label={i18n.displayEquipments}
                                        checked={displayEquipment}
                                        onChange={setDisplayEquipment}
                                    />
                                )}
                            </Grid>
                            <Button
                                variant='contained'
                                onClick={() => onSearch()}
                            >
                                {i18n.search}
                            </Button>
                            <Grid container size={2} justifyContent={'flex-end'}>
                                {view === MAP_PANEL && (
                                    <Tooltip title={i18n.table}><List sx={{ fontSize: '35px', color: 'secondary.main', cursor: 'pointer' }} onClick={() => changeView(LIST_PANEL)}/></Tooltip>
                                )}
                                {view === LIST_PANEL && (
                                    <Tooltip title={i18n.map}><Map sx={{ fontSize: '35px', color: 'secondary.main', cursor: 'pointer' }} onClick={() => changeView(MAP_PANEL)}/></Tooltip>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

SimSubscriptionFilterForm.propTypes = {
    view: PropTypes.string,
    filter: PropTypes.shape({
        searchValue: PropTypes.string,
        network: PropTypes.string,
        state: PropTypes.string,
        administrator: PropTypes.number,
        provider: PropTypes.number,
        matType: PropTypes.number,
        displayEquipment: PropTypes.bool,
        displayOutOfService: PropTypes.bool,
    }),
    changeView: PropTypes.func,
    materiels: PropTypes.arrayOf(PropTypes.shape({
        statusCode: PropTypes.number,
        providerId: PropTypes.number,
        administratorId: PropTypes.number,
        networkCode: PropTypes.number,
    })),
    typeList: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        label: PropTypes.string,
    })),
    onValidate: PropTypes.func,
    showEquipmentCheck: PropTypes.boolean,
    materielType: PropTypes.string,
}

export default SimSubscriptionFilterForm